import { useMergedRef } from '@mantine/hooks';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { disabledNativeFieldClasses } from '../utils/fieldStyleClasses';
import { setValidInputDimension } from '../utils/validateStyleDimension';
import type { InputLengthStyleProps, TextFieldProps } from './TextField.types';

const errorClasses = 'tw:border-sq-danger';

const borderColorClasses = [
  'tw:border-sq-disabled-gray',
  'tw:dark:border-sq-dark-disabled-gray',
  'tw:hover:border-sq-theme-dark',
  'tw:dark:hover:border-sq-theme-dark-dark',
  'tw:dark:focus:border-sq-theme-dark-dark',
  'tw:dark:active:border-sq-theme-darker',
  'tw:focus:border-sq-theme-dark',
  'tw:active:border-sq-theme-dark',
].join(' ');

const baseClasses =
  'tw:h-8.5 tw:leading-normal tw:outline-none tw:py-1 tw:px-3' +
  ' tw:p-1 tw:border-solid tw:border tw:placeholder-gray-400!' +
  ' tw:dark:placeholder-sq-dark-text/30! specTextField';

const darkTheme = 'tw:dark:bg-sq-dark-background tw:dark:text-sq-dark-text';
const lightTheme = 'tw:text-sq-text-color';

const sizeClasses = {
  sm: 'tw:text-sm',
  lg: 'tw:text-xl',
};
/**
 * Textfield.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps & InputLengthStyleProps>((props, ref) => {
  const {
    readonly = false,
    disabled = false,
    onChange,
    onPaste,
    onKeyUp,
    onFocus,
    onBlur,
    onKeyDown,
    id,
    name,
    size = 'sm',
    value,
    placeholder,
    extraClassNames = '',
    testId,
    type = 'text',
    inputGroup,
    step,
    showError,
    errorText,
    min,
    max,
    maxLength,
    minLength,
    required = false,
    autoComplete = 'off',
    inputWidth = undefined,
    inputHeight = undefined,
    autoFocus = false,
    ...tooltipProps
  } = props;

  const internalRef = useRef<HTMLInputElement | null>(null);
  const mergedRef = useMergedRef(ref, internalRef);
  const [cursor, setCursor] = useState<number | null>(null);
  const tooltipData = getQTipData(tooltipProps);
  const initialAutoFocusRef = useRef(autoFocus);

  useEffect(() => {
    if (initialAutoFocusRef.current) {
      setTimeout(() => {
        internalRef.current?.focus();
      }, 0);
    }
  }, []);

  useEffect(() => {
    const input = internalRef.current;
    if (input && type !== 'number' && type !== 'email') input.setSelectionRange(cursor, cursor);
  }, [ref, cursor, value, type]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setCursor(e.target.selectionStart);
    onChange && onChange(e);
  };

  const shouldResetNumberStepBase = () => {
    if (type !== 'number') return false;
    if (min === undefined) return true;

    return min % Number(step ?? 1) === 0;
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (shouldResetNumberStepBase()) {
      const input = e.currentTarget;
      const blurValue = input.value;

      input.defaultValue = '';
      input.value = input.validity.valid && value !== null && value !== undefined ? `${value}` : blurValue;
    }
    onBlur?.(e);
  };

  // Number inputs are uncontrolled (see valueProp below) — sync external value changes
  // to the DOM here. Skip while focused so we don't clobber the user mid-type.
  useEffect(() => {
    const input = internalRef.current;

    if (type !== 'number') return;
    if (value !== null && value !== undefined && value !== input?.value && input) {
      input.value = `${value}`;
    }
  }, [value, type]);

  let borderRadius = 'tw:rounded-md';
  if (inputGroup === 'left') {
    borderRadius = 'tw:rounded-l-md tw:border-r-0 tw:focus:border-r tw:active:border-r';
  } else if (inputGroup === 'right') {
    borderRadius = 'tw:rounded-r-md tw:border-l-0 tw:focus:border-l tw:active:border-l';
  }

  const appliedClasses = `${baseClasses} ${disabledNativeFieldClasses} ${
    sizeClasses[size]
  } ${extraClassNames} ${lightTheme} ${darkTheme} ${borderRadius} ${showError ? errorClasses : borderColorClasses} `;

  const inputProp = setValidInputDimension(inputWidth, inputHeight)
    ? {
        style: setValidInputDimension(inputWidth, inputHeight),
      }
    : {};
  const inputLenghtProp: InputLengthStyleProps = {};
  if (maxLength) inputLenghtProp.maxLength = maxLength;
  if (minLength) inputLenghtProp.minLength = minLength;

  // CRAB-59299: number inputs use `defaultValue` (uncontrolled) so React doesn't keep
  // rewriting the value attribute — that attribute is the browser's step base, and
  // controlled writes caused wrong arrow increments / step-mismatch errors. `handleBlur`
  // clears defaultValue so the step base falls back to `min`/0.
  const valueProp = type === 'number' ? { defaultValue: value } : { value };

  return (
    <>
      <input
        ref={mergedRef}
        data-testid={testId}
        name={name}
        id={id}
        type={type}
        {...valueProp}
        className={appliedClasses}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readonly}
        autoComplete={autoComplete}
        onChange={handleChange}
        onPaste={onPaste}
        onFocus={onFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        step={step}
        required={required}
        min={min}
        max={max}
        {...inputLenghtProp}
        {...inputProp}
        {...tooltipData}
      />
      {errorText && showError && <div className="tw:text-sq-danger tw:text-xs tw:mt-1">{errorText}</div>}
    </>
  );
});
TextField.displayName = 'TextField';
